import React, { useState } from 'react';
import { Formik } from 'formik';
import { Link, NavigateFunction, useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../store/store';
import Loader from '../../../components/Common/Loader';
import { Toast } from '../../../components/Common/Toast';
import { confirmChildBook } from '../services/Student.service';
import { CONFIG } from '../../../config/mainConfig';

export interface ParentChildConfirmationRequest {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string;
  confirm: boolean;
  marketing?: boolean;
  receiveUpdates?: boolean;
}

// Create a simplified signup schema for email/password only
const ParentChildConfirmationSchema = () => {
  return Yup.object().shape({
    firstName: Yup.string().required('First name is required'),
    lastName: Yup.string().required('Last name is required'),
    email: Yup.string().email('Invalid email address').required('Email is required'),
    phoneNumber: Yup.string().optional(),
    confirm: Yup.boolean().oneOf([true], 'You must accept the terms and conditions'),
    marketing: Yup.boolean(),
    receiveUpdates: Yup.boolean(),
  });
};

const ParentChildConfirmation = () => {
  const dispatch: AppDispatch = useDispatch();
  const navigateTo: NavigateFunction = useNavigate();
  const currentMainURLQuery = useSelector((state: RootState) => state.app.currentMainURLQuery);
  const { bookId } = useParams();

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleSignUpFormSubmit = async (values: any) => {
    if (!bookId) {
      Toast('Book ID is not set. Please check the URL or try again.', true);
      return;
    }
    try {
      setIsLoading(true);
      const queryParams = new URLSearchParams(currentMainURLQuery);
      await confirmChildBook(
        bookId,
        queryParams.get(CONFIG.HEADERS.BOOK_SHARE),
        {
          firstName: values.firstName.trim(),
          lastName: values.lastName.trim(),
          email: values.email.trim().toLowerCase(),
          phoneNumber: values.phoneNumber,
          confirm: values.confirm,
          marketing: values.marketing,
          receiveUpdates: values.receiveUpdates,
        },
        navigateTo,
      );
    } catch (error: any) {
      Toast(error?.message || 'Failed to confirm', true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col font-sofiaSans justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-sm py-8 px-4 sm:px-10">
        <div className="text-left mb-6">
          <h1 className="text-3xl font-bold text-black mb-3">View Your Child’s Book</h1>
          <p className="text-black">
            Your child is now a published author! Order their book to take part in the celebration.
          </p>
        </div>

        <div className="">
          <Formik
            initialValues={{
              firstName: '',
              lastName: '',
              email: '',
              phoneNumber: '',
              confirm: false,
              marketing: false,
              receiveUpdates: false,
            }}
            validationSchema={ParentChildConfirmationSchema}
            onSubmit={(values) => {
              handleSignUpFormSubmit(values);
            }}
          >
            {({ errors, touched, values, handleChange, handleSubmit, setFieldValue }) => (
              <form className="space-y-6" onSubmit={handleSubmit}>
                <div className="grid grid-cols-2 gap-4">
                  <div className="relative">
                    <input
                      type="text"
                      name="firstName"
                      value={values.firstName}
                      onChange={handleChange}
                      placeholder="First Name"
                      className="appearance-none block w-full px-4 py-2 border border-secondaryStoryGrayBorder rounded-lg placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-secondaryStoryAdditionalLightBlueText focus:border-secondaryStoryAdditionalLightBlueText text-base text-secondaryStoryBlackText"
                    />
                    {touched.firstName && errors.firstName && (
                      <div className="absolute text-storyseconderror text-sm mt-0.5">{errors.firstName}</div>
                    )}
                  </div>
                  <div className="relative">
                    <input
                      type="text"
                      name="lastName"
                      value={values.lastName}
                      onChange={handleChange}
                      placeholder="Last Name"
                      className="appearance-none block w-full px-4 py-2 border border-secondaryStoryGrayBorder rounded-lg placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-secondaryStoryAdditionalLightBlueText focus:border-secondaryStoryAdditionalLightBlueText text-base text-secondaryStoryBlackText"
                    />
                    {touched.lastName && errors.lastName && (
                      <div className="absolute text-storyseconderror text-sm mt-0.5">{errors.lastName}</div>
                    )}
                  </div>
                </div>

                <div className="relative">
                  <input
                    type="email"
                    name="email"
                    value={values.email}
                    onChange={handleChange}
                    placeholder="School email (Recommended)"
                    className="appearance-none block w-full px-4 py-2 border border-secondaryStoryGrayBorder rounded-lg placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-secondaryStoryAdditionalLightBlueText focus:border-secondaryStoryAdditionalLightBlueText text-base text-secondaryStoryBlackText"
                  />
                  {touched.email && errors.email && (
                    <div className="absolute text-storyseconderror text-sm mt-0.5">{errors.email}</div>
                  )}
                </div>

                <div className="relative">
                  <PhoneInput
                    // country={'us'}
                    value={values.phoneNumber}
                    onChange={(phoneNumber) => setFieldValue('phoneNumber', phoneNumber)}
                    placeholder="Mobile Number (Optional)"
                    inputClass="!w-full !px-4 !pl-12 !py-2 !border !border-secondaryStoryGrayBorder !rounded-lg !text-base !text-secondaryStoryBlackText"
                    containerClass="!w-full"
                    buttonClass="!border !border-secondaryStoryGrayBorder !rounded-l-lg !bg-white hover:!bg-gray-50"
                    dropdownClass="!border !border-secondaryStoryGrayBorder !rounded-lg !shadow-lg"
                    // disableCountryCode={false}
                    // countryCodeEditable={false}
                    // disableCountryGuess={false}
                    preferredCountries={['us', 'ca', 'gb', 'au']}
                    inputStyle={{
                      width: '100%',
                      height: '42px',
                      fontSize: '16px',
                      paddingLeft: '48px',
                      border: '1px solid',
                      borderRadius: '8px',
                      outline: 'none',
                    }}
                    containerStyle={{
                      width: '100%',
                    }}
                    buttonStyle={{
                      border: '1px solid',
                      borderRadius: '8px 0 0 8px',
                      backgroundColor: 'white',
                    }}
                  />
                  {touched.phoneNumber && errors.phoneNumber && (
                    <div className="absolute text-storyseconderror text-sm mt-0.5">{errors.phoneNumber}</div>
                  )}
                </div>

                <div className="flex flex-col gap-1.5">
                  <div className="flex text-left">
                    <input
                      id="confirm"
                      name="confirm"
                      type="checkbox"
                      checked={values.confirm}
                      onChange={(e) => setFieldValue('confirm', e.target.checked)}
                      className={`h-4 w-4 mt-0.5 focus:ring-secondaryStoryAdditionalLightBlueText rounded border-none text-secondaryStoryAdditionalLightBlueText`}
                    />
                    <label
                      htmlFor="confirm"
                      className={`ml-2 block text-sm ${touched.confirm && errors.confirm ? 'text-storyseconderror' : 'text-secondaryDescriptionTextColor'} `}
                    >
                      <span className="font-bold mr-1">Required:</span>I confirm I am the child’s parent or legal
                      guardian and I give permission for them to use Build My Story. I agree to the{' '}
                      <Link
                        to="https://buildmystory.com/terms-and-conditions/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-secondaryStoryAdditionalLightBlueText hover:underline"
                      >
                        Terms of Use
                      </Link>{' '}
                      and{' '}
                      <Link
                        to="https://buildmystory.com/privacy-policy/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-secondaryStoryAdditionalLightBlueText hover:underline"
                      >
                        Privacy Policy
                      </Link>
                    </label>
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <div className="flex text-left">
                      <input
                        id="marketing"
                        name="marketing"
                        type="checkbox"
                        checked={values.marketing}
                        onChange={(e) => setFieldValue('marketing', e.target.checked)}
                        className={`h-4 w-4 mt-0.5 focus:ring-secondaryStoryAdditionalLightBlueText rounded border-none text-secondaryStoryAdditionalLightBlueText`}
                      />
                      <label
                        htmlFor="marketing"
                        className={`ml-2 block text-sm ${touched.confirm && errors.confirm ? 'text-storyseconderror' : 'text-secondaryDescriptionTextColor'} `}
                      >
                        <span className="font-bold mr-1">Optional:</span>Yes, you can share my child’s book to help
                        inspire other kids, including on your site, social, or promos.
                      </label>
                    </div>
                    <div className="flex text-left">
                      <input
                        id="receiveUpdates"
                        name="receiveUpdates"
                        type="checkbox"
                        checked={values.receiveUpdates}
                        onChange={(e) => setFieldValue('receiveUpdates', e.target.checked)}
                        className="h-4 w-4 mt-0.5 text-secondaryStoryAdditionalLightBlueText focus:ring-secondaryStoryAdditionalLightBlueText border-secondaryStoryGrayBorder rounded"
                      />
                      <label htmlFor="marketing" className="ml-2 block text-sm text-secondaryDescriptionTextColor">
                        Yes, I’d like to receive tips, free resources, and occasional updates.
                      </label>
                    </div>
                  </div>

                  <div>
                    <button
                      type="submit"
                      disabled={isLoading}
                      className="w-full flex items-center justify-center py-1 px-4 border border-transparent rounded-lg shadow-sm text-lg font-bold text-white bg-secondaryStoryAdditionalLightBlueText hover:shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Continue
                      <Loader loader={isLoading} />
                    </button>
                  </div>
                </div>
              </form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
};

export default ParentChildConfirmation;
